import { useState, useCallback } from 'react';
import { updateVehicle } from '../api/vehicleService';
import { saveDispatchReservation } from '../api/dispatchService';
import type { Vehicle, StatusItem, Reservation } from '../types';

const CHECK_FIELDS = ['cleaning', 'car_wash', 'check1', 'check2', 'tire_groove', 'tire_pressure'];

interface StatusModalState {
  open: boolean;
  vehicle: Vehicle | null;
  reservation: Reservation | null;
}

interface LocationStatusModalState {
  open: boolean;
  vehicle: Vehicle | null;
  listType: string | null;
  reservation: Reservation | null;
}

interface ConfirmDialogState {
  open: boolean;
  vehicle: Vehicle | null;
  statusId: number | null;
  reservation: Reservation | null;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: string;
}

interface User {
  id: string | number;
  [key: string]: any;
}

interface UseStatusDialogsOptions {
  user: User | null;
  vehicleStatusList: StatusItem[];
  locationStatusList: StatusItem[];
  setVehicles: React.Dispatch<React.SetStateAction<Record<string, Vehicle>>>;
  setDepartures: React.Dispatch<React.SetStateAction<Reservation[]>>;
  setReturns: React.Dispatch<React.SetStateAction<Reservation[]>>;
}

export function useStatusDialogs({
  user,
  vehicleStatusList,
  locationStatusList,
  setVehicles,
  setDepartures,
  setReturns,
}: UseStatusDialogsOptions) {
  const [statusModal, setStatusModal] = useState<StatusModalState>({ open: false, vehicle: null, reservation: null });
  const [locationStatusModal, setLocationStatusModal] = useState<LocationStatusModalState>({ open: false, vehicle: null, listType: null, reservation: null });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({ open: false, vehicle: null, statusId: null, reservation: null });
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'success' });

  const handleCloseToast = useCallback(() => {
    setToast(prev => ({ ...prev, open: false }));
  }, []);

  const executeStatusUpdate = useCallback(async (vehicle: Vehicle, statusId: number, clearChecks: boolean, reservation: Reservation | null = null) => {
    const statusObj = vehicleStatusList.find(s => s.id === statusId);
    const newStatusName = statusObj ? statusObj.label : '';

    const updates: Record<string, any> = { status: statusId };
    if (clearChecks) {
      if (vehicle.check_situations && Array.isArray(vehicle.check_situations)) {
        updates.check_situations = vehicle.check_situations.map((item: any) => ({ ...item, value: false }));
      } else {
        CHECK_FIELDS.forEach(field => {
          updates[field] = false;
        });
      }
    }

    const updatedVehicle = { ...vehicle, ...updates, status_name: newStatusName };
    if (updates.check_situations) {
      updatedVehicle.check_situations = updates.check_situations;
    }

    setVehicles(prev => ({ ...prev, [vehicle.id]: updatedVehicle }));

    try {
      await updateVehicle({ id: vehicle.id, ...updates }, user?.id);

      // statusId=1（出発中）に変更 → 対応する予約を「利用中」(3) に自動更新
      if (statusId === 1 && reservation) {
        if (String(reservation.status) !== '3' && reservation.status !== 'in_use') {
          try {
            await saveDispatchReservation({ ...reservation, status: 3, status_code: 'in_use' } as any);
            const updateRes = (r: Reservation) => (String(r.id) === String(reservation.id) ? { ...r, status: 3, status_code: 'in_use' } as any : r);
            setDepartures(prev => prev.map(updateRes));
            setReturns(prev => prev.map(updateRes));
            setToast({ open: true, message: '車両を出発中に変更し、予約内容を利用中に更新しました', severity: 'success' });
          } catch (resError) {
            console.error('Failed to auto-update reservation status:', resError);
            setToast({ open: true, message: '車両ステータスを更新しました（予約の自動更新に失敗しました）', severity: 'warning' });
          }
          return;
        }
      }

      setToast({ open: true, message: 'ステータスを更新しました', severity: 'success' });

      // 出発中(1)から別の状態に変更 → 対応する予約を「貸出済」(2) に自動更新
      if (vehicle.status === 1 as any && statusId !== 1 && reservation) {
        if (String(reservation.status) !== '2' && reservation.status !== 'done') {
          try {
            await saveDispatchReservation({ ...reservation, status: 2, status_code: 'done' } as any);
            const updateRes = (r: Reservation) => (String(r.id) === String(reservation.id) ? { ...r, status: 2, status_code: 'done' } as any : r);
            setDepartures(prev => prev.map(updateRes));
            setReturns(prev => prev.map(updateRes));
            setToast({ open: true, message: '車両を返却済みに変更し、予約内容を貸出済(完了)に更新しました', severity: 'success' });
          } catch (resError) {
            console.error('Failed to auto-update reservation status:', resError);
            setToast({ open: true, message: '車両ステータスを更新しました（予約の自動更新に失敗しました）', severity: 'warning' });
          }
        }
      }
    } catch (error) {
      console.error('Failed to update vehicle status:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setVehicles(prev => ({ ...prev, [vehicle.id]: vehicle }));
    }
  }, [user, vehicleStatusList, setVehicles, setDepartures, setReturns]);

  const handleStatusClick = useCallback((vehicle: Vehicle, reservation: Reservation | null) => {
    setStatusModal({ open: true, vehicle, reservation });
  }, []);

  const handleStatusUpdate = useCallback((statusId: number) => {
    const { vehicle, reservation } = statusModal;
    if (!vehicle) return;

    setStatusModal({ open: false, vehicle: null, reservation: null });

    if (statusId === 1 && vehicle.status !== 1 as any) {
      setConfirmDialog({ open: true, vehicle, statusId, reservation });
      return;
    }

    executeStatusUpdate(vehicle, statusId, false, reservation);
  }, [statusModal, executeStatusUpdate]);

  const handleConfirmClose = useCallback((clearChecks: boolean) => {
    const { vehicle, statusId, reservation } = confirmDialog;
    setConfirmDialog({ open: false, vehicle: null, statusId: null, reservation: null });
    if (vehicle && statusId) {
      executeStatusUpdate(vehicle, statusId, clearChecks, reservation);
    }
  }, [confirmDialog, executeStatusUpdate]);

  const handleLocationStatusClick = useCallback((vehicle: Vehicle, listType: string, reservation: Reservation | null) => {
    setLocationStatusModal({ open: true, vehicle, listType, reservation });
  }, []);

  const handleLocationStatusUpdate = useCallback(async (locationStatusId: number) => {
    const vehicle = locationStatusModal.vehicle;
    if (!vehicle) return;

    setLocationStatusModal({ open: false, vehicle: null, listType: null, reservation: null });

    const locationStatus = locationStatusList.find(s => s.id === locationStatusId);
    const newLocationStatusName = locationStatus?.label || null;

    const updatedVehicle = {
      ...vehicle,
      location_status: locationStatusId,
      location_status_name: newLocationStatusName,
      location_status_code: locationStatus?.code || null,
    };
    setVehicles(prev => ({ ...prev, [vehicle.id]: updatedVehicle }));

    try {
      await updateVehicle({ id: vehicle.id, location_status: locationStatusId } as any, user?.id);
      setToast({ open: true, message: '移動ステータスを更新しました', severity: 'success' });
    } catch (error) {
      console.error('Failed to update location status:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setVehicles(prev => ({ ...prev, [vehicle.id]: vehicle }));
    }
  }, [locationStatusModal, locationStatusList, user, setVehicles]);

  return {
    statusModal,
    setStatusModal,
    locationStatusModal,
    setLocationStatusModal,
    confirmDialog,
    toast,
    setToast,
    handleCloseToast,
    executeStatusUpdate,
    handleStatusClick,
    handleStatusUpdate,
    handleConfirmClose,
    handleLocationStatusClick,
    handleLocationStatusUpdate,
  };
}
